import React, { useState, useEffect } from 'react';
import { languages, getTranslation } from './languages';
import { User, Cake, GraduationCap, ChevronDown, FileText } from 'lucide-react';
import { renderMarkdown } from './utils/markdownUtils';

export default function UserProfileModal({ isOpen, onClose, onSaveProfile, currentLanguage = 'en', googleUserData = null, onLanguageChange }) {
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [ageRange, setAgeRange] = useState('');
    const [educationLevel, setEducationLevel] = useState('');
    const [modalLanguage, setModalLanguage] = useState(currentLanguage);
    const [isSkipping, setIsSkipping] = useState(false);
    const [readmeContent, setReadmeContent] = useState('');

    useEffect(() => {
        if (googleUserData && googleUserData.name) {
            setName(googleUserData.name);
        }
    }, [googleUserData]);

    useEffect(() => {
        const fetchReadme = async () => {
            try {
                const response = await fetch(`/docs/README_${modalLanguage}.md`);
                if (response.ok) {
                    const text = await response.text();
                    setReadmeContent(text.trim());
                } else {
                    // Fallback to English if the language-specific one isn't found
                    const fallbackResponse = await fetch(`/docs/README_en.md`);
                    if (fallbackResponse.ok) {
                        const text = await fallbackResponse.text();
                        setReadmeContent(text.trim());
                    }
                }
            } catch (error) {
                console.error("Failed to fetch README:", error);
            }
        };

        if (isOpen) {
            fetchReadme();
        }
    }, [modalLanguage, isOpen]);

    const t = (key, params = {}) => getTranslation(key, modalLanguage, params);

    const handleLanguageSelect = (lang) => {
        setModalLanguage(lang);
        if (onLanguageChange) {
            onLanguageChange(lang);
        }
    };

    const handleNext = () => {
        setStep(prev => prev + 1);
    };

    // Calculate birth year based on age range
    const calculateBirthYear = (ageRange) => {
        if (!ageRange || ageRange === 'prefer_not_to_say') return null;
        
        const currentYear = new Date().getFullYear();
        const ageRanges = {
            'under20': { min: 0, max: 19 },
            '20-30': { min: 20, max: 30 },
            '31-40': { min: 31, max: 40 },
            '41-50': { min: 41, max: 50 },
            '51-60': { min: 51, max: 60 },
            'over60': { min: 61, max: 100 }
        };
        
        const range = ageRanges[ageRange];
        if (!range) return null;
        
        // Use the middle of the range for calculation
        const middleAge = Math.floor((range.min + range.max) / 2);
        return currentYear - middleAge;
    };

    const handleSave = async () => {
        const birthYear = ageRange ? calculateBirthYear(ageRange) : null;
        
        // Save profile first
        await onSaveProfile({
            name: name || null,
            ageRange: ageRange || null,
            birthYear,
            educationLevel: educationLevel || null,
            uiLanguage: modalLanguage
        });
        
        // Send welcome email after profile is saved (with correct language)
        try {
            const sessionId = localStorage.getItem('sessionId');
            if (sessionId) {
                const response = await fetch('/api/send-welcome-email', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionId}`
                    }
                });
                
                const data = await response.json();
                if (data.success && !data.alreadySent) {
                    console.log('Welcome email sent successfully');
                }
            }
        } catch (error) {
            console.error('Failed to send welcome email:', error);
            // Don't block the user if email fails
        }
    };

    const handleSkip = async () => {
        setIsSkipping(true);
        
        // Save profile with minimal data
        await onSaveProfile({
            name: name || null, // Keep the name even when skipping
            ageRange: null,
            birthYear: null,
            educationLevel: null,
            uiLanguage: modalLanguage, // Still save the UI language as it's mandatory
            profileSkipped: true
        });
        
        // Send welcome email even when skipping (they've selected language)
        try {
            const sessionId = localStorage.getItem('sessionId');
            if (sessionId) {
                const response = await fetch('/api/send-welcome-email', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionId}`
                    }
                });
                
                const data = await response.json();
                if (data.success && !data.alreadySent) {
                    console.log('Welcome email sent successfully');
                }
            }
        } catch (error) {
            console.error('Failed to send welcome email:', error);
            // Don't block the user if email fails
        }
    };

    // Note: renderMarkdown is now imported from utils/markdownUtils.js
    // We create a wrapper to apply custom classes for UserProfileModal
    const renderMarkdownWrapper = (content) => {
        const customClasses = "prose prose-sm max-w-none prose-h1:font-bold prose-h1:text-2xl prose-h1:mt-0 prose-h1:mb-4 prose-p:text-gray-600 prose-headings:text-gray-800 prose-h2:mt-6 prose-h2:mb-3 prose-h3:mt-5 prose-h3:mb-2";
        return renderMarkdown(content, { customClasses });
    };

    if (!isOpen) return null;

    const totalSteps = 3;

    return (
        <div 
            className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm z-50 flex justify-center items-center p-4"
        >
            <div className="bg-gray-50 rounded-xl w-full max-w-3xl shadow-2xl animate-fade-in-up overflow-hidden">
                {/* Progress Bar */}
                <div className="px-8 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-700">
                            {step === 1 && t('selectPreferredLanguage')}
                            {step === 2 && t('quickStartGuide')}
                            {step === 3 && t('userProfileSetup')}
                        </h3>
                        <span className="text-sm font-semibold text-gray-500">
                            {t('step')} <span className="text-gray-900">{step}</span> / {totalSteps}
                        </span>
                    </div>
                    <div className="overflow-hidden h-2 text-xs flex rounded-full bg-gray-200">
                        <div style={{ width: `${(step / totalSteps) * 100}%` }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-sky-500 rounded-full transition-all duration-500"></div>
                    </div>
                </div>
                
                <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${(step - 1) * 100}%)` }}>
                    {/* Step 1: Language Selection */}
                    <div className="w-full flex-shrink-0 p-8">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold mb-2 text-gray-800">{t('welcomeToApp', { appTitle: t('appTitle') })}</h2>
                            <p className="text-gray-600">{t('selectPreferredLanguage')}</p>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            {Object.entries(languages).map(([key, lang]) => (
                                <button
                                    key={key}
                                    onClick={() => handleLanguageSelect(key)}
                                    className={`p-4 border-2 rounded-lg text-center transition-all duration-200 ${modalLanguage === key ? 'bg-sky-50 border-sky-500 shadow-md' : 'bg-white border-gray-200 hover:border-sky-400 hover:shadow-sm'}`}
                                >
                                    <span className="font-semibold text-gray-700 text-lg">{lang.nativeName}</span>
                                </button>
                            ))}
                        </div>

                        <div className="flex justify-end mt-8">
                            <button
                                onClick={handleNext}
                                className="w-full md:w-auto px-8 py-3 rounded-lg text-white font-semibold bg-sky-500 hover:bg-sky-600 transition-colors shadow-sm hover:shadow-md"
                            >
                                {t('next')}
                            </button>
                        </div>
                    </div>

                    {/* Step 2: README */}
                    <div className="w-full flex-shrink-0 px-8 py-6 flex flex-col" style={{ height: '75vh', maxHeight: '550px' }}>
                        <div className="flex items-center gap-3 mb-4 flex-shrink-0">
                            <FileText className="w-5 h-5 text-sky-500" />
                            <h2 className="text-lg font-bold text-gray-800">{t('quickStartGuide')}</h2>
                        </div>
                        <div className="flex-grow overflow-hidden bg-white rounded-lg border border-gray-200 shadow-inner p-1">
                            <div className="overflow-y-auto h-full px-6 pb-6 custom-scrollbar">
                                {renderMarkdownWrapper(`# ${t('appTitle')}\n${readmeContent}`)}
                            </div>
                        </div>
                        <div className="flex justify-between mt-8 flex-shrink-0">
                            <button
                                onClick={() => setStep(step - 1)}
                                className="px-8 py-3 rounded-lg text-gray-700 font-semibold bg-gray-200 hover:bg-gray-300 transition-colors"
                            >
                                {t('back')}
                            </button>
                            <button
                                onClick={handleNext}
                                className="px-8 py-3 rounded-lg text-white font-semibold bg-sky-500 hover:bg-sky-600 transition-colors shadow-sm hover:shadow-md"
                            >
                                {t('next')}
                            </button>
                        </div>
                    </div>

                    {/* Step 3: Profile Details */}
                    <div className="w-full flex-shrink-0 p-8">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold mb-2 text-gray-800">{t('userProfileSetup')}</h2>
                            <p className="text-gray-600">{t('profileSetup')}</p>
                        </div>

                        <div className="space-y-6">
                            {/* Name */}
                            <div>
                                <label className="text-sm font-bold text-gray-700 block mb-2">
                                    {t('name')} <span className="text-gray-500 font-normal">({t('optional')})</span>
                                </label>
                                <div className="relative">
                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        placeholder={t('enterYourName')}
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1.5 px-1">{t('nameHelp')}</p>
                            </div>

                            {/* Age Range */}
                            <div>
                                <label className="text-sm font-bold text-gray-700 block mb-2">
                                    {t('ageRange')} <span className="text-gray-500 font-normal">({t('optional')})</span>
                                </label>
                                <div className="relative">
                                    <Cake className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <select
                                        value={ageRange}
                                        onChange={(e) => setAgeRange(e.target.value)}
                                        className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-lg bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-sky-500"
                                    >
                                        <option value="">{t('selectAgeRange')}</option>
                                        {Object.entries(t('ageRanges')).map(([key, value]) => (
                                            <option key={key} value={key}>{value}</option>
                                        ))}
                                        <option value="" disabled>──────────</option>
                                        <option value="prefer_not_to_say">{t('preferNotToSay')}</option>
                                    </select>
                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                                <p className="text-xs text-gray-500 mt-1.5 px-1">{t('ageRangeHelp')}</p>
                            </div>

                            {/* Education Level */}
                            <div>
                                <label className="text-sm font-bold text-gray-700 block mb-2">
                                    {t('educationLevel')} <span className="text-gray-500 font-normal">({t('optional')})</span>
                                </label>
                                <div className="relative">
                                    <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <select
                                        value={educationLevel}
                                        onChange={(e) => setEducationLevel(e.target.value)}
                                        className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-lg bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-sky-500"
                                    >
                                        <option value="">{t('selectEducation')}</option>
                                        {Object.entries(t('educationLevels')).map(([key, value]) => (
                                            <option key={key} value={key}>{value}</option>
                                        ))}
                                        <option value="" disabled>──────────</option>
                                        <option value="prefer_not_to_say">{t('preferNotToSay')}</option>
                                    </select>
                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                                <p className="text-xs text-gray-500 mt-1.5 px-1">{t('educationLevelHelp')}</p>
                            </div>
                        </div>

                        <div className="flex justify-between items-center mt-8">
                            <button
                                onClick={() => setStep(step - 1)}
                                className="px-8 py-3 rounded-lg text-gray-700 font-semibold bg-gray-200 hover:bg-gray-300 transition-colors"
                            >
                                {t('back')}
                            </button>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={handleSkip}
                                    disabled={isSkipping}
                                    className="text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    {isSkipping ? '...' : t('skipForNow')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-8 py-3 rounded-lg text-white font-semibold bg-sky-500 hover:bg-sky-600 transition-colors shadow-sm hover:shadow-md"
                                >
                                    {t('saveProfile')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
} 